import React, { useState, useEffect, useContext } from 'react';
import { Link } from '@this/components/shared/ui/navigations/link';
import { Modal, ModalHeader, ModalBody } from '@this/components/shared/ui/feedbacks/modal';
import Box from '@material-ui/core/Box';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Loading } from '@this/components/shared/ui/feedbacks/loading';
import { Fetcher } from '@this/src/util';
import OrderItemCalendarEvent, {
  convertOrderItemCalendarEventResponseToArgs
} from '@this/src/domain/order_item_calendar_event';
import type { OrderItemCalendarEventResponseArgs } from '@this/src/domain/order_item_calendar_event';
import type OrderItem from '@this/src/domain/order_item2';
import CoreAppContext from '@this/components/core_app/core_app_context';
import type Trip from '@this/domain/trip/trip2';
import { createCalendarLink, formatMainContentBodyText, formatDate, isAllday } from '../trip_calendar_helpers';
import type { Theme } from '../trip_calendar_helpers';
import { CalendarLinkButton } from './calendar_link_button';

type CalendarProvider = 'google' | 'outlook';

interface CalendarAddButtonProps {
  provider: CalendarProvider;
  trip: Trip;
  user: any;
  theme: Theme;
  orderItem: OrderItem;
  registeredCalendarEvents?: { [key: string]: any };
}

interface ProviderConfig {
  iconPath: string;
  buttonText: string;
  authUrl: string;
  authButtonText: string;
  fallbackText: string;
  connectedKey: 'google_calendar_connected' | 'outlook_calendar_connected';
  urlParam: string;
}

const CalendarAddButton: React.FC<CalendarAddButtonProps> = ({
  provider,
  trip,
  user,
  theme,
  orderItem,
  registeredCalendarEvents
}) => {
  const { serviceId } = useContext(CoreAppContext);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registeredCalendarEvent, setRegisteredCalendarEvent] = useState<OrderItemCalendarEvent | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const config: ProviderConfig = {
    google: {
      iconPath: '/images/google-icon.png',
      buttonText: 'Googleカレンダーに追加',
      authUrl: '/users/calendar_auth/google',
      authButtonText: 'Googleカレンダー連携',
      fallbackText: '→連携せずにGoogleカレンダーのサイト上で新規作成',
      connectedKey: 'google_calendar_connected' as const,
      urlParam: 'google_calendar_connected'
    },
    outlook: {
      iconPath: '/images/outlook-icon.png',
      buttonText: 'Outlookカレンダーに追加',
      authUrl: '/users/calendar_auth/outlook',
      authButtonText: 'Outlookカレンダー連携',
      fallbackText: '→連携せずにOutlookカレンダーのサイト上で新規作成',
      connectedKey: 'outlook_calendar_connected' as const,
      urlParam: 'outlook_calendar_connected'
    }
  }[provider];

  useEffect(() => {
    if (orderItem?.orderItemCalendarEvents) {
      const existingEvent = orderItem.orderItemCalendarEvents.find(
        event => event.connectedCalendar?.provider === provider && event.userId === user.id
      );
      if (existingEvent) {
        setRegisteredCalendarEvent(existingEvent);
      }
    }
  }, [orderItem, provider, user.id]);

  useEffect(() => {
    if (registeredCalendarEvents && orderItem?.traceId) {
      const event = registeredCalendarEvents[orderItem.traceId];
      if (event && event.connectedCalendar?.provider === provider && event.userId === user.id) {
        setRegisteredCalendarEvent(event);
      }
    }
  }, [registeredCalendarEvents, orderItem?.traceId, provider, user.id]);

  const handleCalendarAddClick = async () => {
    const isCalendarConnected = !!user?.[config.connectedKey];

    if (!isCalendarConnected) {
      setShowAuthModal(true);
    } else {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await Fetcher.post<OrderItemCalendarEventResponseArgs>(
          `/trips/${trip.id}/calendar_events.json`,
          {
            trace_id: orderItem?.traceId,
            calendar: provider
          }
        );

        setRegisteredCalendarEvent(
          new OrderItemCalendarEvent(convertOrderItemCalendarEventResponseToArgs(response))
        );
      } catch (error: any) {
        console.error(`[${provider}] Calendar registration failed:`, error);

        // 400エラー（連携解除）の場合は連携を求めるモーダルを表示
        if (error.response?.status === 400) {
          setShowAuthModal(true);
        } else {
          const errorMsg =
            error.response?.data?.error ||
            'カレンダーへの予定登録に失敗しました。時間をおいて再度お試しください。';
          setErrorMessage(errorMsg);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const hideAuthModal = () => {
    setShowAuthModal(false);
  };

  const handleCalendarAuth = () => {
    let currentUrl = window.location.href;
    const traceId = orderItem?.traceId;

    if (!currentUrl.includes(`${config.urlParam}=true`)) {
      if (currentUrl.includes('?')) {
        currentUrl += `&${config.urlParam}=true`;
      } else {
        currentUrl += `?${config.urlParam}=true`;
      }
    }

    if (traceId) {
      const traceIdParam = `trace_id=${encodeURIComponent(traceId)}`;
      if (!currentUrl.includes(traceIdParam)) {
        if (currentUrl.includes('?')) {
          currentUrl += `&${traceIdParam}`;
        } else {
          currentUrl += `?${traceIdParam}`;
        }
      }
    }

    window.location.href = `${config.authUrl}?redirect_to=${encodeURIComponent(currentUrl)}`;
  };

  // 登録済みかどうかの判定
  const isRegistered = !!registeredCalendarEvent;

  const calendarLink = createCalendarLink(
    orderItem.mappingDescription,
    formatMainContentBodyText(orderItem, trip, user.id, theme, serviceId, provider),
    formatDate(orderItem, provider),
    formatDate(orderItem, provider, true),
    provider,
    isAllday(orderItem)
  );

  const capitalizedProvider = provider.charAt(0).toUpperCase() + provider.slice(1);

  return (
    <>
      {serviceId !== 1 ? (
        <CalendarLinkButton href={calendarLink} disabled={orderItem.isCanceled}>
          <img
            src={config.iconPath}
            style={{
              width: '14px',
              height: '14px',
              marginRight: '4px'
            }}
            alt={capitalizedProvider}
          />
          <span
            style={{
              fontSize: '11px',
              letterSpacing: '0'
            }}
          >
            {config.buttonText}
          </span>
        </CalendarLinkButton>
      ) : isRegistered ? (
        <CalendarLinkButton href={registeredCalendarEvent?.htmlLink || '#'} disabled={orderItem.isCanceled}>
          <img
            src={config.iconPath}
            style={{
              width: '14px',
              height: '14px',
              marginRight: '4px'
            }}
            alt={`${capitalizedProvider} Calendar`}
          />
          <span
            style={{
              fontSize: '11px',
              letterSpacing: '0'
            }}
          >
            予定登録済み<span style={{ color: '#28a745', marginLeft: '4px' }}>✓</span>
          </span>
        </CalendarLinkButton>
      ) : (
        <CalendarLinkButton
          onClick={isLoading ? undefined : handleCalendarAddClick}
          disabled={orderItem.isCanceled}
        >
          <img
            src={config.iconPath}
            style={{
              width: '14px',
              height: '14px',
              marginRight: '4px'
            }}
            alt={`${capitalizedProvider} Calendar`}
          />
          <span
            style={{
              fontSize: '11px',
              letterSpacing: '0'
            }}
          >
            {isLoading ? <Loading size="small" centerd={false} /> : config.buttonText}
          </span>
        </CalendarLinkButton>
      )}

      {errorMessage && (
        <div
          style={{
            color: 'red',
            fontSize: '11px',
            marginTop: '4px'
          }}
        >
          {errorMessage}
        </div>
      )}

      <Modal open={showAuthModal} onClose={hideAuthModal}>
        <ModalHeader>カレンダー連携</ModalHeader>
        <ModalBody>
          <p>カレンダーに予定を登録するには、カレンダーの連携をしてください</p>
          <Box mt={2}>
            <Button color="primary" onClick={handleCalendarAuth}>
              {config.authButtonText}
            </Button>
          </Box>
          <Box mt={4}>
            {orderItem && (
              <Link href={calendarLink} target="_blank" rel="noopener noreferrer">
                {config.fallbackText}
              </Link>
            )}
          </Box>
        </ModalBody>
      </Modal>
    </>
  );
};

export default CalendarAddButton;
